import styled from 'styled-components'
import React, { useContext } from 'react'
import { GridContainer, GridItem } from '@entur/grid'
import { Heading4, Label, Paragraph, SmallText } from '@entur/typography'
import { ActionChip } from '@entur/chip'
import { colors, space } from '@entur/tokens'
import { SmallAlertBox } from '@entur/alert'
import { IconButton } from '@entur/button'
import { CloseIcon, DeleteIcon, HorizontalDotsIcon, MoneyIcon } from '@entur/icons'
import { OverflowMenu, OverflowMenuItem } from '@entur/menu'
import { Badge } from '@entur/layout'

import { FamilyMember, FetchState, TicketRight } from '../../../api-clients/api-types'
import { InfoCard } from '../../card/card'
import { toPrintFormat } from '../../../utils/date-utils'
import {
  useCategory,
  useDisabilityProof,
  useFeePaid,
  useLowIncome,
} from '../../../hooks/employee-yearly-data-hooks'
import { useGenerateTicketRights } from '../../../hooks/use-ticket-rights'
import { capitalize } from '../../../utils/string-utils'
import { DisabledContext } from '../../../contexts/disable-context'
import { useUpdateManualOrder } from '../../../hooks/use-manual-order'
import { nullValue } from '../../../utils/form-utils'
import { TicketRightCategoryBadge } from '../ticket-right-category-badge'
import DeleteModal from '../../delete-modal/delete-modal'
import { useClientInfo } from '../../../contexts/client-info-context'
import { ReadOnlyContext } from '../../../contexts/read-only-context'

export const CardActionChip = styled(ActionChip)`
  float: left;
  margin-right: ${space.medium}px;
`
type TicketRightCardBaseProps = {
  ticketRight: TicketRight
  actionChips?: React.ReactNode
  badges?: React.ReactNode
  errorMessage?: string
  categoryName: string
  children: React.ReactNode
  disabilityProofRegistered?: boolean
  isFeePaid?: boolean
  canBeDeleted?: boolean
  familyMember?: FamilyMember
}
export const TicketRightCardBase: React.FC<TicketRightCardBaseProps> = ({
  ticketRight,
  actionChips,
  badges,
  errorMessage,
  categoryName,
  children,
  disabilityProofRegistered,
  isFeePaid,
  canBeDeleted = true,
  familyMember,
}) => {
  const { hasSensitiveAccess } = useClientInfo()
  const { disabled } = useContext(DisabledContext)
  const { readOnly } = useContext(ReadOnlyContext)
  const { deleteManualOrderResponse, fetchUpdatedEmployeeResponse, deleteManualOrder } = useUpdateManualOrder()
  const year = parseInt(ticketRight.validFrom.split('-')[0], 10)
  const [generateTicketRightsResponse, generateTicketRights] = useGenerateTicketRights(() =>
    setDeleteModalOpen(false)
  )
  const [setCategoryResponse, setCategory] = useCategory(year, () => generateTicketRights())
  const [lowIncomeResponse, setLowIncome] = useLowIncome(year - 2, familyMember, generateTicketRights)
  const [isDeleteModalOpen, setDeleteModalOpen] = React.useState(false)
  const [addFeeResponse, setFeePaid] = useFeePaid(year, () => generateTicketRights())
  const [addDisabilityProofResponse, setDisabilityProof] = useDisabilityProof(year, generateTicketRights)

  const isStationTicket = ticketRight?.manualOrder?.ticketType?.canHaveStationPairs
  const lowIncomeRegistered = familyMember?.lowIncomes?.find((it) => it.year === year - 2)?.isRegistered

  const title = isStationTicket
    ? `Strekningsrettighet (${capitalize(categoryName)})`
    : `Årsrettighet (${capitalize(categoryName)})`

  const top = (
    <div style={{ marginTop: -space.medium }}>
      {ticketRight.ticketType.code !== 'NO' ? (
        <TicketRightCategoryBadge ticketRight={ticketRight} categoryName={categoryName} />
      ) : (
        <Badge variant="danger" style={{ display: 'inline-block' }}>
          INGEN RETTIGHET
        </Badge>
      )}
      <SmallText
        style={{
          color: colors.blues.blue40,
          marginLeft: space.extraSmall,
          float: 'right',
          padding: '0.125rem',
        }}
      >
        Opprettet: {toPrintFormat(ticketRight.calculationTimestamp)}
      </SmallText>
    </div>
  )

  const header = <Heading4 style={{ marginTop: space.medium }}>{title}</Heading4>
  const btn =
    !readOnly && hasSensitiveAccess === true ? (
      <IconButton style={{ marginTop: space.medium }} name="ticket-right-card-menu-btn">
        <HorizontalDotsIcon />
      </IconButton>
    ) : (
      <></>
    )
  const iconBtn =
    disabilityProofRegistered || isFeePaid || canBeDeleted || familyMember ? (
      <OverflowMenu button={btn}>
        {disabilityProofRegistered && (
          <OverflowMenuItem
            onSelect={() => setDisabilityProof(false)}
            className="ticket-right-card-menu-delete-disability-poof"
          >
            <span aria-hidden style={{ whiteSpace: 'nowrap' }}>
              <CloseIcon inline />
              Slett uføreattest
            </span>
          </OverflowMenuItem>
        )}
        {isFeePaid && (
          <OverflowMenuItem onSelect={() => setFeePaid(false)} className="ticket-right-card-menu-delete-fee">
            <span aria-hidden style={{ whiteSpace: 'nowrap' }}>
              <CloseIcon inline />
              Slett egenandel
            </span>
          </OverflowMenuItem>
        )}
        {familyMember && familyMember?.relation.code !== 'Barn' && (
          <OverflowMenuItem
            onSelect={() => setLowIncome(!lowIncomeRegistered)}
            className="ticket-right-card-set-low-income"
          >
            <span aria-hidden style={{ whiteSpace: 'nowrap' }}>
              {lowIncomeRegistered ? <CloseIcon inline /> : <MoneyIcon inline />}
              {lowIncomeRegistered ? ' Avregistrer lav inntekt' : ' Registrer lav inntekt'}
            </span>
          </OverflowMenuItem>
        )}
        {canBeDeleted && (
          <OverflowMenuItem
            onSelect={() => setDeleteModalOpen(true)}
            className="ticket-right-card-menu-delete-ticket-right"
          >
            <span aria-hidden style={{ whiteSpace: 'nowrap' }}>
              <DeleteIcon inline />
              Slett rettighet
            </span>
          </OverflowMenuItem>
        )}
      </OverflowMenu>
    ) : (
      <></>
    )

  const deleteModal = (
    <DeleteModal
      title="Slette rettighet?"
      description={`Er du sikker på at du vil slette ${ticketRight.ticketType.description}`}
      negativeText="Slett rettighet"
      positiveText="Behold rettighet"
      action={() => {
        if (ticketRight?.manualOrder) {
          deleteManualOrder(ticketRight?.manualOrder?.id)
          setDeleteModalOpen(false)
        } else {
          setCategory(nullValue)
        }
      }}
      open={isDeleteModalOpen}
      setOpen={setDeleteModalOpen}
    />
  )

  let error
  if (errorMessage) error = errorMessage
  if (setCategoryResponse.status === FetchState.ERROR || generateTicketRightsResponse.status === FetchState.ERROR)
    error = 'Rettigheten ble ikke slettet'
  if (
    deleteManualOrderResponse.status === FetchState.ERROR &&
    deleteManualOrderResponse.error instanceof Error &&
    deleteManualOrderResponse.error.message !== 'Not Found'
  )
    error = 'Rettigheten ble ikke slettet'
  if (lowIncomeResponse.status === FetchState.ERROR) error = 'Endring av lav inntekt feilet'
  if (fetchUpdatedEmployeeResponse.status === FetchState.ERROR)
    error = 'Henting av endret rettighet feilet, last siden på nytt'
  if (addFeeResponse.status === FetchState.ERROR) error = 'Lagring av egenandel feilet på grunn av teknisk feil'
  if (addDisabilityProofResponse.status === FetchState.ERROR)
    error = 'Lagring av uføreattest feilet på grunn av teknisk feil'

  return (
    <InfoCard
      header={header}
      iconButtons={iconBtn}
      top={top}
      className={`ticket-right-card ${disabled ? 'disabled' : ''}`}
    >
      {deleteModal}
      <GridContainer rowSpacing="medium" spacing="small">
        <GridItem small={6}>
          <Label>Periode</Label>
          <Paragraph style={{ marginBottom: 0 }}>
            {toPrintFormat(ticketRight.validFrom)} - {toPrintFormat(ticketRight.validUntil)}
          </Paragraph>
        </GridItem>
        <GridItem small={6}>{children}</GridItem>
        {badges && <GridItem small={12}>{badges}</GridItem>}
        {error && (
          <GridItem small={12} style={{ marginTop: 0 }}>
            <SmallAlertBox variant="error">{error}</SmallAlertBox>
          </GridItem>
        )}
        {actionChips && <GridItem small={12}>{actionChips}</GridItem>}
      </GridContainer>
    </InfoCard>
  )
}
